import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import api, { setToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, XCircle } from "lucide-react";

export default function AcceptInvitation() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [full_name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm_password, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setErr("No invitation token provided."); return; }
    api.get(`/auth/invitation?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setErr(e.response?.data?.detail || "This invitation is invalid or expired."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm_password) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invitation", { token, full_name, password, confirm_password });
      if (data.access_token) setToken(data.access_token);
      setUser(data.user);
      toast.success(data.message || "Welcome to the team!");
      nav("/dashboard");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not accept invitation");
    } finally { setLoading(false); }
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="w-full max-w-md bg-white border rounded-2xl shadow-sm p-8 text-center" data-testid="invite-error">
          <XCircle className="mx-auto text-red-500" size={44} />
          <h2 className="mt-4 text-xl font-bold text-slate-900">Invitation problem</h2>
          <p className="mt-2 text-sm text-slate-500">{err}</p>
          <Link to="/login" className="inline-block mt-6 text-blue-600 hover:underline text-sm">Go to sign in</Link>
        </div>
      </div>
    );
  }
  if (!info) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <form onSubmit={submit} className="w-full max-w-md bg-white border rounded-2xl shadow-sm p-8" data-testid="accept-invite-form">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Join {info.account_name}</h2>
        <p className="text-sm text-slate-500 mt-1.5">You've been invited as <span className="font-medium capitalize">{info.role}</span>. Set your name and password to continue.</p>
        <div className="mt-6 space-y-4">
          <div className="space-y-1.5"><Label>Email</Label><Input value={info.email} disabled className="h-11 bg-slate-50" /></div>
          <div className="space-y-1.5"><Label>Full name</Label><Input data-testid="invite-name" value={full_name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-11" /></div>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <div className="relative">
              <Input data-testid="invite-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" className="h-11 pr-10" />
              <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Confirm password</Label><Input data-testid="invite-confirm" type={showPw ? "text" : "password"} value={confirm_password} onChange={(e) => setConfirm(e.target.value)} className="h-11" /></div>
          <Button type="submit" data-testid="invite-submit" disabled={loading} className="w-full h-11">{loading ? "Joining…" : "Join account"}</Button>
        </div>
      </form>
    </div>
  );
}
